import React, { useState, useEffect, useRef } from "react";
import {
    Container,
    Paper,
    Typography,
    Box,
    TextField,
    Button,
    Divider,
    Alert,
    CircularProgress,
    Grid,
    Card,
    CardContent,
    Switch,
    FormControlLabel,
    Tabs,
    Tab,
    Input,
    IconButton,
    Snackbar,
    InputAdornment,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    AppBar,
    Toolbar   
} from "@mui/material";
import {
    Save as SaveIcon,
    Business as BusinessIcon,
    LocationOn as LocationOnIcon,
    VpnKey as VpnKeyIcon,
    CloudUpload as CloudUploadIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    Email as EmailIcon,
    PersonAdd as PersonAddIcon,
    Security as SecurityIcon,
    Tune as TuneIcon,
    AccessTime as AccessTimeIcon,
    Edit as EditIcon,
    SupervisorAccount as SupervisorAccountIcon,
    ArrowBack as ArrowBackIcon,
    Phone as PhoneIcon,
    Language as LanguageIcon,
    ContentCopy as ContentCopyIcon,
    Block as BlockIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import BlockedDatesManager from "../components/BlockedDatesManager";
import { auth, db } from "../firebase";
import {
    updatePassword,
    reauthenticateWithCredential,
    EmailAuthProvider
} from "firebase/auth";
import {
    doc,
    getDoc,
    updateDoc,
    collection,
    query,
    where,
    getDocs,
    serverTimestamp
} from "firebase/firestore";

// Import admin feature components
import TimeEntryCorrections from "../components/TimeEntryCorrections";
import AdminClockActions from "../components/AdminClockActions";
import UserRoleManagement from "../components/UserRoleManagement";
import QRGenerator from "../components/QRGenerator";
import IPLocationSetup from "../components/IPLocationSetup";
import QRScanner from "../components/QRScanner";
import AdminManualClockActions from "../components/AdminManualClockActions";
// TabPanel component for tab content
function TabPanel({ children, value, index, ...other }) {
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`settings-tabpanel-${index}`}
            aria-labelledby={`settings-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

const AdminSettingsPage = ({ user, orgId }) => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const fileInputRef = useRef(null);

    const [orgData, setOrgData] = useState({
        name: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        country: "",
        phone: "",
        website: "",
        industry: "",
        companySize: "",
        logoUrl: ""
    });

    const [passwordData, setPasswordData] = useState({
        currentPassword: "",
        newPassword: "",
        confirmPassword: ""
    });

    const [workLocations, setWorkLocations] = useState([]);
    const [newLocation, setNewLocation] = useState({
        name: "",
        address: "",
        wifiSsid: "",
        radius: 100, // Default radius in meters
        isActive: true
    });

    const [verificationSettings, setVerificationSettings] = useState({
        requireLocationVerification: true,
        allowIPVerification: true,      
        allowQrCodeVerification: true,
        allowGpsVerification: false,
        strictMode: false,
        verificationTimeout: 15 
    });

    const [timeoffSettings, setTimeoffSettings] = useState({
        requireApproval: true,
        autoApproveIfUnderDays: 1,
        maxConsecutiveDays: 14,
        maxDaysPerYear: 21,
        allowNegativeBalance: false,
        defaultNewEmployeeBalance: 14
    });

    const [generatedInviteLink, setGeneratedInviteLink] = useState("");
    const [inviteEmail, setInviteEmail] = useState("");

    const [showPassword, setShowPassword] = useState({
        current: false,
        new: false,
        confirm: false
    });

    const [alert, setAlert] = useState({
        open: false,
        message: "",
        severity: "info"
    });

    const [errors, setErrors] = useState({});
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState("");
    // Fetch organization data
    useEffect(() => {
        const fetchOrgData = async () => {
            if (!orgId) return;

            setLoading(true);
            try {
                const orgRef = doc(db, "organizations", orgId);
                const orgDoc = await getDoc(orgRef);

                if (orgDoc.exists()) {
                    const data = orgDoc.data();
                    setOrgData(prev => ({
                        ...prev,
                        name: data.name || "",
                        address: data.address || "",
                        city: data.city || "",
                        state: data.state || "",
                        zip: data.zip || "",
                        country: data.country || "",
                        phone: data.phone || "",
                        website: data.website || "",
                        industry: data.industry || "",
                        companySize: data.companySize || "",
                        logoUrl: data.logoUrl || ""
                    }));

                    if (data.logoUrl) {
                        setLogoPreview(data.logoUrl);
                    }

                    // Load verification settings if they exist
                    if (data.verificationSettings) {
                        setVerificationSettings(prev => ({
                            ...prev,
                            ...data.verificationSettings
                        }));
                    }

                    // Load time-off settings if they exist
                    if (data.timeoffSettings) {
                        setTimeoffSettings(prev => ({
                            ...prev,
                            ...data.timeoffSettings
                        }));
                    }
                }

                // Fetch work locations
                const locationsRef = collection(db, "organizations", orgId, "locations");
                const locationsSnapshot = await getDocs(locationsRef);
                const locationsData = locationsSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));

                setWorkLocations(locationsData);

            } catch (error) {
                console.error("Error fetching organization data:", error);
                setAlert({
                    open: true,
                    message: "Failed to load organization data",
                    severity: "error"
                });
            } finally {
                setLoading(false);
            }
        };

        fetchOrgData();
    }, [orgId]);

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const handleOrgDataChange = (e) => {
        const { name, value } = e.target;
        setOrgData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleNewLocationChange = (e) => {
        const { name, value } = e.target;
        setNewLocation(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleNewLocationSwitchChange = (e) => {
        const { name, checked } = e.target;
        setNewLocation(prev => ({
            ...prev,
            [name]: checked
        }));
    };

    const handleVerificationSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        setVerificationSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleTimeoffSettingsChange = (e) => {
        const { name, value, type, checked } = e.target;
        const parsedValue = type === 'number' ? parseInt(value, 10) : value;

        setTimeoffSettings(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : parsedValue
        }));
    };
    const togglePasswordVisibility = (field) => {
        setShowPassword(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const handleLogoChange = (e) => {
        if (e.target.files.length > 0) {
            const file = e.target.files[0];
            setLogoFile(file);

            // Create preview
            const reader = new FileReader();
            reader.onload = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const clearLogoSelection = () => {
        setLogoFile(null);
        setLogoPreview(orgData.logoUrl || "");
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const validateOrgData = () => {
        const newErrors = {};

        if (!orgData.name.trim()) {
            newErrors.name = "Organization name is required";
        }

        // Add any other validations as needed

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validatePasswordData = () => {
        const newErrors = {};

        if (!passwordData.currentPassword) {
            newErrors.currentPassword = "Current password is required";
        }

        if (passwordData.newPassword.length < 6) {
            newErrors.newPassword = "Password must be at least 6 characters";
        }

        if (passwordData.newPassword !== passwordData.confirmPassword) {
            newErrors.confirmPassword = "Passwords do not match";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateNewLocation = () => {
        const newErrors = {};

        if (!newLocation.name.trim()) {
            newErrors.locationName = "Location name is required";
        }

        if (!newLocation.address.trim()) {
            newErrors.locationAddress = "Address is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const validateInviteEmail = () => {
        const newErrors = {};

        if (!inviteEmail.includes('@')) {
            newErrors.inviteEmail = "Valid email is required";
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const saveOrgProfile = async () => {
        if (!validateOrgData()) return;

        setLoading(true);
        try {
            const orgRef = doc(db, "organizations", orgId);

            // Prepare update data
            const updateData = {
                name: orgData.name,
                address: orgData.address,
                city: orgData.city,
                state: orgData.state,
                zip: orgData.zip,
                country: orgData.country,
                phone: orgData.phone,
                website: orgData.website,
                industry: orgData.industry,
                companySize: orgData.companySize,
                updatedAt: serverTimestamp()
            };

            // Handle logo upload if changed
            if (logoFile) {
                // In a real implementation, you would:
                // 1. Upload the file to Firebase Storage
                // 2. Get the download URL
                // 3. Add it to updateData.logoUrl
                updateData.logoUrl = logoPreview; // In reality, this would be the storage URL
                console.log("Logo file would be uploaded to storage");
            }

            await updateDoc(orgRef, updateData);

            setAlert({
                open: true,
                message: "Organization profile updated successfully",
                severity: "success"
            });
        } catch (error) {
            console.error("Error updating organization profile:", error);
            setAlert({
                open: true,
                message: "Failed to update organization profile",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const changePassword = async () => {
        if (!validatePasswordData()) return;

        setLoading(true);
        try {
            // Re-authenticate user
            const credential = EmailAuthProvider.credential(
                user.email,
                passwordData.currentPassword
            );

            await reauthenticateWithCredential(auth.currentUser, credential);

            // Update password
            await updatePassword(auth.currentUser, passwordData.newPassword);

            // Clear password fields
            setPasswordData({
                currentPassword: "",
                newPassword: "",
                confirmPassword: ""
            });

            setAlert({
                open: true,
                message: "Password changed successfully",
                severity: "success"
            });
        } catch (error) {
            console.error("Error changing password:", error);

            if (error.code === 'auth/wrong-password') {
                setErrors({ ...errors, currentPassword: "Current password is incorrect" });
            } else {
                setAlert({
                    open: true,
                    message: error.message || "Failed to change password",
                    severity: "error"
                });
            }
        } finally {
            setLoading(false);
        }
    };
    const addWorkLocation = async () => {
        if (!validateNewLocation()) return;

        setLoading(true);
        try {
            const locationsRef = collection(db, "organizations", orgId, "locations");

            // Check if location with same name already exists
            const existingLocQuery = query(locationsRef, where("name", "==", newLocation.name));
            const existingLocSnapshot = await getDocs(existingLocQuery);

            if (!existingLocSnapshot.empty) {
                setErrors({ ...errors, locationName: "Location with this name already exists" });
                setLoading(false);
                return;
            }

            // Add new location
            const newLocationRef = doc(locationsRef);
            await updateDoc(newLocationRef, {
                ...newLocation,
                createdAt: serverTimestamp(),
                createdBy: user.uid
            });

            // Update local state
            setWorkLocations([
                ...workLocations,
                {
                    id: newLocationRef.id,
                    ...newLocation
                }
            ]);

            // Reset form
            setNewLocation({
                name: "",
                address: "",
                wifiSsid: "",
                radius: 100,
                isActive: true
            });

            setAlert({
                open: true,
                message: "Work location added successfully",
                severity: "success"
            });
        } catch (error) {
            console.error("Error adding work location:", error);
            setAlert({
                open: true,
                message: "Failed to add work location",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const deleteWorkLocation = async (locationId) => {
        setLoading(true);
        try {
            const locationRef = doc(db, "organizations", orgId, "locations", locationId);
            await updateDoc(locationRef, {
                isActive: false,
                deletedAt: serverTimestamp(),
                deletedBy: user.uid
            });

            // Update local state
            setWorkLocations(workLocations.map(loc =>
                loc.id === locationId
                    ? { ...loc, isActive: false }
                    : loc
            ));

            setAlert({
                open: true,
                message: "Work location deactivated",
                severity: "success"
            });
        } catch (error) {
            console.error("Error deleting work location:", error);
            setAlert({
                open: true,
                message: "Failed to delete work location",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };
    const saveVerificationSettings = async () => {
        setLoading(true);
        try {
            const orgRef = doc(db, "organizations", orgId);

            await updateDoc(orgRef, {
                verificationSettings: {
                    ...verificationSettings,
                    // Ensure we're using the correct field names
                    allowIPVerification: verificationSettings.allowIPVerification
                },
                updatedAt: serverTimestamp()
            });

            setAlert({
                open: true,
                message: "Verification settings saved successfully",
                severity: "success"
            });
        } catch (error) {
            console.error("Error saving verification settings:", error);
            setAlert({
                open: true,
                message: "Failed to save verification settings",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const saveTimeOffSettings = async () => {
        setLoading(true);
        try {
            const orgRef = doc(db, "organizations", orgId);

            await updateDoc(orgRef, {
                timeoffSettings,
                updatedAt: serverTimestamp()
            });

            setAlert({
                open: true,
                message: "Time-off settings saved successfully",
                severity: "success"
            });
        } catch (error) {
            console.error("Error saving time-off settings:", error);
            setAlert({
                open: true,
                message: "Failed to save time-off settings",
                severity: "error"
            });
        } finally {
            setLoading(false);
        }
    };

    const generateInviteLink = () => {
        if (!validateInviteEmail()) return;

        // In a real implementation, you would:
        // 1. Generate a unique invitation code
        // 2. Store it in Firestore with the email
        // 3. Return a URL with the code

        const mockInviteCode = Math.random().toString(36).substring(2, 10);
        const inviteLink = `${window.location.origin}/invite/${orgId}/${encodeURIComponent(inviteEmail)}?code=${mockInviteCode}`;

        setGeneratedInviteLink(inviteLink);
    };

    const handleCloseAlert = () => {
        setAlert({ ...alert, open: false });
    };
    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        TimeWarden
                    </Typography>
                    <Button
                        color="inherit"
                        startIcon={<ArrowBackIcon />}
                        onClick={() => navigate("/admin")}
                    >
                        Back to Dashboard
                    </Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="lg">
                <Paper sx={{ mt: 5, p: 4 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                        <BusinessIcon sx={{ mr: 2, color: 'primary.main' }} />
                        <Typography variant="h5">Organization Settings</Typography>
                    </Box>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                        <Tabs
                            value={tabValue}
                            onChange={handleTabChange}
                            variant="scrollable"
                            scrollButtons="auto"
                        >
                            <Tab label="Organization Profile" icon={<BusinessIcon />} iconPosition="start" />
                            <Tab label="Work Locations" icon={<LocationOnIcon />} iconPosition="start" />
                            <Tab label="Verification Settings" icon={<SecurityIcon />} iconPosition="start" />
                            <Tab label="Time-off Settings" icon={<TuneIcon />} iconPosition="start" />
                            <Tab label="Admin Account" icon={<VpnKeyIcon />} iconPosition="start" />
                            <Tab label="Invite Employees" icon={<PersonAddIcon />} iconPosition="start" />                            

                            {/* New Admin Features Tabs */}
                            <Tab label="Time Corrections" icon={<EditIcon />} iconPosition="start" />
                            <Tab label="Clock Actions" icon={<AccessTimeIcon />} iconPosition="start" />                            
                            <Tab label="Role Management" icon={<SupervisorAccountIcon />} iconPosition="start" />
                            <Tab label="Blocked Dates" icon={<BlockIcon />} iconPosition="start" />
                        </Tabs>
                    </Box>

                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {/* Organization Profile Tab */}
                    <TabPanel value={tabValue} index={0}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={4}>
                                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', p: 3 }}>
                                    <Typography variant="h6" gutterBottom>Company Logo</Typography>
                                    <Box
                                        sx={{
                                            width: '100%',
                                            height: 200,
                                            border: '1px dashed grey',
                                            borderRadius: 1,
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            mb: 2,
                                            position: 'relative',
                                            overflow: 'hidden'
                                        }}
                                    >
                                        {logoPreview ? (
                                            <Box
                                                component="img"
                                                src={logoPreview}
                                                sx={{
                                                    maxWidth: '100%',
                                                    maxHeight: '100%',
                                                    objectFit: 'contain'
                                                }}
                                            />
                                        ) : (
                                            <Typography color="text.secondary">No logo</Typography>
                                        )}
                                    </Box>

                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        onChange={handleLogoChange}
                                    />

                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="contained"
                                            startIcon={<CloudUploadIcon />}
                                            onClick={() => fileInputRef.current.click()}
                                            disabled={loading}
                                        >
                                            Upload Logo
                                        </Button>

                                        {logoFile && (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                onClick={clearLogoSelection}
                                                disabled={loading}
                                            >
                                                Cancel
                                            </Button>
                                        )}
                                    </Box>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={8}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>Organization Details</Typography>
                                        <Divider sx={{ mb: 3 }} />

                                        <Grid container spacing={2}>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    label="Organization Name"
                                                    name="name"
                                                    value={orgData.name}
                                                    onChange={handleOrgDataChange}
                                                    error={!!errors.name}
                                                    helperText={errors.name}
                                                    disabled={loading}
                                                    required
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Street Address"
                                                    name="address"
                                                    value={orgData.address}
                                                    onChange={handleOrgDataChange}
                                                    disabled={loading}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="City"
                                                    name="city"
                                                    value={orgData.city}
                                                    onChange={handleOrgDataChange}
                                                    disabled={loading}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    fullWidth
                                                    label="State/Province"
                                                    name="state"
                                                    value={orgData.state}
                                                    onChange={handleOrgDataChange}
                                                    disabled={loading}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Postal/ZIP Code"
                                                    name="zip"
                                                    value={orgData.zip}
                                                    onChange={handleOrgDataChange}
                                                    disabled={loading}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={4}>
                                                <TextField
                                                    fullWidth
                                                    label="Country"
                                                    name="country"
                                                    value={orgData.country}
                                                    onChange={handleOrgDataChange}
                                                    disabled={loading}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Phone Number"
                                                    name="phone"
                                                    value={orgData.phone}
                                                    onChange={handleOrgDataChange}
                                                    disabled={loading}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <PhoneIcon />
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <TextField
                                                    fullWidth
                                                    label="Website"
                                                    name="website"
                                                    value={orgData.website}
                                                    onChange={handleOrgDataChange}
                                                    disabled={loading}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <LanguageIcon />
                                                            </InputAdornment>
                                                        )
                                                    }}
                                                />
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth disabled={loading}>
                                                    <InputLabel>Industry</InputLabel>
                                                    <Select
                                                        name="industry"
                                                        value={orgData.industry}
                                                        onChange={handleOrgDataChange}
                                                        label="Industry"
                                                    >
                                                        <MenuItem value="">Select...</MenuItem>
                                                        <MenuItem value="technology">Technology</MenuItem>
                                                        <MenuItem value="healthcare">Healthcare</MenuItem>
                                                        <MenuItem value="education">Education</MenuItem>
                                                        <MenuItem value="finance">Finance</MenuItem>
                                                        <MenuItem value="retail">Retail</MenuItem>
                                                        <MenuItem value="manufacturing">Manufacturing</MenuItem>
                                                        <MenuItem value="services">Services</MenuItem>
                                                        <MenuItem value="other">Other</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>

                                            <Grid item xs={12} sm={6}>
                                                <FormControl fullWidth disabled={loading}>
                                                    <InputLabel>Company Size</InputLabel>
                                                    <Select
                                                        name="companySize"
                                                        value={orgData.companySize}
                                                        onChange={handleOrgDataChange}
                                                        label="Company Size"
                                                    >
                                                        <MenuItem value="">Select...</MenuItem>
                                                        <MenuItem value="1-10">1-10 employees</MenuItem>
                                                        <MenuItem value="11-50">11-50 employees</MenuItem>
                                                        <MenuItem value="51-200">51-200 employees</MenuItem>
                                                        <MenuItem value="201-500">201-500 employees</MenuItem>
                                                        <MenuItem value="501-1000">501-1000 employees</MenuItem>
                                                        <MenuItem value="1001+">1001+ employees</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </Grid>
                                        </Grid>

                                        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                startIcon={<SaveIcon />}
                                                onClick={saveOrgProfile}
                                                disabled={loading}
                                            >
                                                Save Changes
                                            </Button>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </TabPanel>

                    {/* Work Locations Tab */}
                    <TabPanel value={tabValue} index={1}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Work Locations</Typography>
                                <Divider sx={{ mb: 3 }} />

                                <Grid container spacing={2}>
                                    {/* List of existing work locations */}
                                    {workLocations.map(location => (
                                        <Grid item xs={12} key={location.id}>
                                            <Card variant="outlined" sx={{ mb: 2 }}>
                                                <CardContent>
                                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                        <Box>
                                                            <Typography variant="subtitle1">{location.name}</Typography>
                                                            <Typography variant="body2" color="text.secondary">{location.address}</Typography>
                                                            {location.wifiSsid && (
                                                                <Typography variant="body2">WiFi: {location.wifiSsid}</Typography>
                                                            )}
                                                        </Box>
                                                        <Box>
                                                            <Button
                                                                variant="outlined"
                                                                color="error"
                                                                size="small"
                                                                onClick={() => deleteWorkLocation(location.id)}
                                                                disabled={loading}
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </Button>
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>

                                {/* Add new location form */}
                                <Box sx={{ mt: 3 }}>
                                    <Typography variant="subtitle1">Add New Location</Typography>
                                    <Grid container spacing={2} sx={{ mt: 1 }}>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Location Name"
                                                name="name"
                                                value={newLocation.name}
                                                onChange={handleNewLocationChange}
                                                error={!!errors.locationName}
                                                helperText={errors.locationName}
                                                disabled={loading}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="Address"
                                                name="address"
                                                value={newLocation.address}
                                                onChange={handleNewLocationChange}
                                                error={!!errors.locationAddress}
                                                helperText={errors.locationAddress}
                                                disabled={loading}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                label="WiFi SSID (Optional)"
                                                name="wifiSsid"
                                                value={newLocation.wifiSsid}
                                                onChange={handleNewLocationChange}
                                                disabled={loading}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={newLocation.isActive}
                                                        onChange={handleNewLocationSwitchChange}
                                                        name="isActive"
                                                        color="primary"
                                                        disabled={loading}
                                                    />
                                                }
                                                label="Active"
                                            />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                onClick={addWorkLocation}
                                                disabled={loading}
                                            >
                                                Add Location
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Box>
                            </CardContent>
                        </Card>
                    </TabPanel>

                    {/* Verification Settings Tab */}
                    <TabPanel value={tabValue} index={2}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Verification Settings</Typography>
                                <Divider sx={{ mb: 3 }} />

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" gutterBottom>Location Verification</Typography>

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={verificationSettings.requireLocationVerification}
                                                    onChange={handleVerificationSettingsChange}
                                                    name="requireLocationVerification"
                                                    color="primary"
                                                    disabled={loading}
                                                />
                                            }
                                            label="Require Location Verification"
                                        />

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={verificationSettings.strictMode}
                                                    onChange={handleVerificationSettingsChange}
                                                    name="strictMode"
                                                    color="primary"
                                                    disabled={loading}
                                                />
                                            }
                                            label="Strict Verification Mode"
                                        />

                                        <Box sx={{ mt: 2 }}>
                                            <TextField
                                                fullWidth
                                                label="Verification Timeout (minutes)"
                                                name="verificationTimeout"
                                                type="number"
                                                value={verificationSettings.verificationTimeout}
                                                onChange={handleVerificationSettingsChange}
                                                disabled={loading}
                                                inputProps={{ min: 1, max: 60 }}
                                            />
                                        </Box>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" gutterBottom>Verification Methods</Typography>

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={verificationSettings.allowIPVerification}
                                                    onChange={handleVerificationSettingsChange}
                                                    name="allowIPVerification"
                                                    color="primary"
                                                    disabled={loading}
                                                />
                                            }
                                            label="Allow IP Location Verification"
                                        />

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={verificationSettings.allowQrCodeVerification}
                                                    onChange={handleVerificationSettingsChange}
                                                    name="allowQrCodeVerification"
                                                    color="primary"
                                                    disabled={loading}
                                                />
                                            }
                                            label="Allow QR Code Verification"
                                        />

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={verificationSettings.allowGpsVerification}
                                                    onChange={handleVerificationSettingsChange}
                                                    name="allowGpsVerification"
                                                    color="primary"
                                                    disabled={loading}
                                                />
                                            }
                                            label="Allow GPS Verification"
                                        />
                                    </Grid>
                                </Grid>

                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SaveIcon />}
                                        onClick={saveVerificationSettings}
                                        disabled={loading}
                                    >
                                        Save Verification Settings
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>

                        {/* QR Code Generator Section */}
                        {verificationSettings.allowQrCodeVerification && (
                            <Box sx={{ mt: 4 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            QR Code Verification
                                        </Typography>
                                        <Divider sx={{ mb: 3 }} />

                                        <QRGenerator orgId={orgId} userId={user.uid} />
                                    </CardContent>
                                </Card>
                            </Box>
                        )}

                        {/* IP VERIFICATION SECTION */}
                        {verificationSettings.allowIPVerification && (
                            <Box sx={{ mt: 4 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            IP-Based Location Verification
                                        </Typography>
                                        <Divider sx={{ mb: 3 }} />

                                        <IPLocationSetup orgId={orgId} userId={user.uid} />
                                    </CardContent>
                                </Card>
                            </Box>
                        )}

                        {/* QR Scanner Test Section */}
                        {verificationSettings.allowQrCodeVerification && (
                            <Box sx={{ mt: 4 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            Test QR Code Scanner
                                        </Typography>
                                        <Divider sx={{ mb: 3 }} />

                                        <QRScanner
                                            orgId={orgId}
                                            onVerificationComplete={(result) => {
                                                setAlert({
                                                    open: true,
                                                    message: result.success
                                                        ? `Successfully verified at: ${result.locationName}`
                                                        : "Verification failed",
                                                    severity: result.success ? "success" : "error"
                                                });
                                            }}
                                        />
                                    </CardContent>
                                </Card>
                            </Box>
                        )}
                    </TabPanel>

                    {/* Time-off Settings Tab */}
                    <TabPanel value={tabValue} index={3}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Time-off Settings</Typography>
                                <Divider sx={{ mb: 3 }} />

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" gutterBottom>General Settings</Typography>

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={timeoffSettings.requireApproval}
                                                    onChange={handleTimeoffSettingsChange}
                                                    name="requireApproval"
                                                    color="primary"
                                                    disabled={loading}
                                                />
                                            }
                                            label="Require Approval for Time-off Requests"
                                        />

                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={timeoffSettings.allowNegativeBalance}
                                                    onChange={handleTimeoffSettingsChange}
                                                    name="allowNegativeBalance"
                                                    color="primary"
                                                    disabled={loading}
                                                />
                                            }
                                            label="Allow Negative Balance"
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Typography variant="subtitle1" gutterBottom>Limits</Typography>

                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            label="Maximum Consecutive Days"
                                            name="maxConsecutiveDays"
                                            type="number"
                                            value={timeoffSettings.maxConsecutiveDays}
                                            onChange={handleTimeoffSettingsChange}
                                            disabled={loading}
                                            inputProps={{ min: 1 }}
                                        />

                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            label="Maximum Days Per Year"
                                            name="maxDaysPerYear"
                                            type="number"
                                            value={timeoffSettings.maxDaysPerYear}
                                            onChange={handleTimeoffSettingsChange}
                                            disabled={loading}
                                            inputProps={{ min: 1 }}
                                        />

                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            label="Auto-approve if Under Days"
                                            name="autoApproveIfUnderDays"
                                            type="number"
                                            value={timeoffSettings.autoApproveIfUnderDays}
                                            onChange={handleTimeoffSettingsChange}
                                            disabled={loading}
                                            inputProps={{ min: 0 }}
                                        />

                                        <TextField
                                            fullWidth
                                            margin="normal"
                                            label="Default Balance for New Employees"
                                            name="defaultNewEmployeeBalance"
                                            type="number"
                                            value={timeoffSettings.defaultNewEmployeeBalance}
                                            onChange={handleTimeoffSettingsChange}
                                            disabled={loading}
                                            inputProps={{ min: 0 }}
                                        />
                                    </Grid>
                                </Grid>

                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        startIcon={<SaveIcon />}
                                        onClick={saveTimeOffSettings}
                                        disabled={loading}
                                    >
                                        Save Time-off Settings
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </TabPanel>

                    {/* Admin Account Tab */}
                    <TabPanel value={tabValue} index={4}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Admin Account Security</Typography>
                                <Divider sx={{ mb: 3 }} />

                                <Typography variant="subtitle1" gutterBottom>Change Password</Typography>

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Current Password"
                                    name="currentPassword"
                                    type={showPassword.current ? "text" : "password"}
                                    value={passwordData.currentPassword}
                                    onChange={handlePasswordChange}
                                    error={!!errors.currentPassword}
                                    helperText={errors.currentPassword}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => togglePasswordVisibility('current')}
                                                    edge="end"
                                                >
                                                    {showPassword.current ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="New Password"
                                    name="newPassword"
                                    type={showPassword.new ? "text" : "password"}
                                    value={passwordData.newPassword}
                                    onChange={handlePasswordChange}
                                    error={!!errors.newPassword}
                                    helperText={errors.newPassword}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => togglePasswordVisibility('new')}
                                                    edge="end"
                                                >
                                                    {showPassword.new ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <TextField
                                    fullWidth
                                    margin="normal"
                                    label="Confirm New Password"
                                    name="confirmPassword"
                                    type={showPassword.confirm ? "text" : "password"}
                                    value={passwordData.confirmPassword}
                                    onChange={handlePasswordChange}
                                    error={!!errors.confirmPassword}
                                    helperText={errors.confirmPassword}
                                    disabled={loading}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => togglePasswordVisibility('confirm')}
                                                    edge="end"
                                                >
                                                    {showPassword.confirm ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }}
                                />

                                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        onClick={changePassword}
                                        disabled={loading}
                                    >
                                        Update Password
                                    </Button>
                                </Box>
                            </CardContent>
                        </Card>
                    </TabPanel>

                    {/* Invite Employees Tab */}
                    <TabPanel value={tabValue} index={5}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Invite Employees</Typography>
                                <Divider sx={{ mb: 3 }} />

                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={8}>
                                        <TextField
                                            fullWidth
                                            label="Email Address"
                                            name="inviteEmail"
                                            type="email"
                                            value={inviteEmail}
                                            onChange={(e) => setInviteEmail(e.target.value)}
                                            error={!!errors.inviteEmail}
                                            helperText={errors.inviteEmail}
                                            disabled={loading}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <EmailIcon />
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            color="primary"
                                            onClick={generateInviteLink}
                                            disabled={loading}
                                            sx={{ height: '56px' }}
                                        >
                                            Generate Invite Link
                                        </Button>
                                    </Grid>
                                </Grid>

                                {generatedInviteLink && (
                                    <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                                        <Typography variant="subtitle1" gutterBottom>Invitation Link</Typography>
                                        <TextField
                                            fullWidth
                                            value={generatedInviteLink}
                                            InputProps={{
                                                readOnly: true,
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => {
                                                                navigator.clipboard.writeText(generatedInviteLink);
                                                                setAlert({
                                                                    open: true,
                                                                    message: "Link copied to clipboard",
                                                                    severity: "success"
                                                                });
                                                            }}
                                                        >
                                                            <ContentCopyIcon />
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                            Share this link with the employee. The link will expire in 7 days.
                                        </Typography>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </TabPanel>

                    {/* NEW TABS: Relocated Admin Features */}

                    {/* Time Corrections Tab */}
                    <TabPanel value={tabValue} index={6}>
                        <Card>
                            <CardContent>
                                <TimeEntryCorrections orgId={orgId} />
                            </CardContent>
                        </Card>
                    </TabPanel>

                    {/* Clock Actions Tab */}
                    <TabPanel value={tabValue} index={7}>
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>Manual Clock Actions</Typography>
                                <Divider sx={{ mb: 3 }} />
                                <AdminManualClockActions user={user} orgId={orgId} />
                            </CardContent>
                        </Card>
                    </TabPanel>

                    {/* Role Management Tab */}
                    <TabPanel value={tabValue} index={8}>
                        <Card>
                            <CardContent>
                                <UserRoleManagement user={user} orgId={orgId} />
                            </CardContent>
                        </Card>
                    </TabPanel>
                    <TabPanel value={tabValue} index={9}>
                        <Card>
                            <CardContent>
                                <BlockedDatesManager orgId={orgId} />
                            </CardContent>
                        </Card>
                    </TabPanel>
                </Paper>

                <Snackbar
                    open={alert.open}
                    autoHideDuration={6000}
                    onClose={handleCloseAlert}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert onClose={handleCloseAlert} severity={alert.severity} sx={{ width: '100%' }}>
                        {alert.message}
                    </Alert>
                </Snackbar>
            </Container>
        </>
    );
};

export default AdminSettingsPage;